<template>
  <div v-if="items && items.length" :class="cls.subIndicators" :style="rootStyle">
    <div
      v-for="(item, index) in items"
      :key="index"
      :class="cls.subIndicator"
      :style="itemStyle"
    >
      <span :class="cls.subIndicatorLabel" :style="labelStyle">{{ item.label }}</span>
      <span :class="cls.subIndicatorValue" :style="getValueStyle(item)">
        {{ renderValue(item) }}
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, type CSSProperties, type PropType, type VNodeChild } from 'vue'
import { useNamespace } from '@jdesign-vue/hooks'
import type { StatisticCardSubIndicator, StatisticCardSize, StatisticVNode } from '../types'
import { createColorVars, createGetTrendColor, STATISTIC_CARD_CLASSES, SUB_INDICATOR_FONT_SIZE } from '../constants'
import { format } from '../utils'

function normalizeValue(value?: string | number | null): number | null {
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isNaN(parsed) ? null : parsed
  }
  return null
}

function renderSubIndicatorValue(
  value?: string | number | null,
  formatter?: (value: StatisticVNode) => StatisticVNode,
): VNodeChild {
  if (value === null || value === undefined) return '—'
  if (formatter) return formatter(value) as VNodeChild
  const numericValue = normalizeValue(value)
  if (numericValue === null) return String(value)
  return format(numericValue, 'percent_2_1_1')
}

export default defineComponent({
  name: 'StatisticCardSubIndicators',
  props: {
    items: { type: Array as PropType<StatisticCardSubIndicator[]>, default: undefined },
    size: { type: String as PropType<StatisticCardSize>, required: true },
    reverseColor: { type: Boolean, default: false },
    layout: { type: String as PropType<'horizontal' | 'vertical'>, default: 'vertical' },
  },
  setup(props) {
    const cls = STATISTIC_CARD_CLASSES
    const ns = useNamespace('statistic-card')
    const colorVars = createColorVars(ns.cssVarName)
    const getTrendColor = createGetTrendColor(colorVars)

    const isVertical = computed(() => props.layout === 'vertical')

    const rootStyle = computed<CSSProperties>(() => ({
      display: 'flex',
      flexDirection: isVertical.value ? 'column' : 'row',
      flexWrap: isVertical.value ? 'nowrap' : 'wrap',
      gap: isVertical.value
        ? `${props.size === 'large' ? 2 : 1}px`
        : `2px ${props.size === 'large' ? 12 : 10}px`,
      fontSize: `${SUB_INDICATOR_FONT_SIZE[props.size]}px`,
      lineHeight: props.size === 'large' ? '22px' : '18px',
    }))

    const itemStyle: CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    }

    const labelStyle: CSSProperties = {
      color: colorVars.textSecondary,
      fontWeight: 400,
    }

    const getValueStyle = (item: StatisticCardSubIndicator): CSSProperties => {
      const numericValue = normalizeValue(item.value)
      const effectiveShowColor = item.showColor ?? (item.formatter === undefined)
      const color =
        effectiveShowColor && numericValue !== null
          ? getTrendColor(numericValue, item.reverseColor ?? props.reverseColor)
          : colorVars.textLabel
      return { color, fontWeight: 600 }
    }

    const renderValue = (item: StatisticCardSubIndicator): VNodeChild =>
      renderSubIndicatorValue(item.value, item.formatter)

    return {
      cls,
      rootStyle,
      itemStyle,
      labelStyle,
      getValueStyle,
      renderValue,
    }
  },
})
</script>